import { useMemo } from 'react'
import { Chart } from 'react-chartjs-2'
import type { ChartData, ChartOptions } from 'chart.js'
import { ensureChartsRegistered } from './register'
import { CHART_COLORS, useDarkMode } from './palette'

ensureChartsRegistered()

export interface ScatterSeries {
  label: string
  points: { x: number; y: number }[]
  color?: string
}

export interface ScatterChartProps {
  series: ScatterSeries[]
  xAxisLabel?: string
  yAxisLabel?: string
  height?: number | string
  className?: string
  ariaLabel?: string
}

const PALETTE = [
  '#2563eb', '#16a34a', '#dc2626', '#ca8a04', '#9333ea',
  '#0891b2', '#ea580c', '#4f46e5', '#db2777', '#0d9488',
]

export function ScatterChart({
  series,
  xAxisLabel,
  yAxisLabel,
  height = 320,
  className,
  ariaLabel,
}: ScatterChartProps) {
  const dark = useDarkMode()

  const data = useMemo<ChartData<'scatter'>>(
    () => ({
      datasets: series.map((s, i) => ({
        label: s.label,
        data: s.points,
        backgroundColor: s.color ?? PALETTE[i % PALETTE.length],
        borderColor: s.color ?? PALETTE[i % PALETTE.length],
        pointRadius: 5,
        pointHoverRadius: 7,
      })),
    }),
    [series],
  )

  const options = useMemo<ChartOptions<'scatter'>>(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        x: {
          type: 'linear',
          title: xAxisLabel ? { display: true, text: xAxisLabel } : undefined,
          grid: { color: dark ? CHART_COLORS.gridDark : CHART_COLORS.gridLight },
          ticks: { color: dark ? CHART_COLORS.tickDark : CHART_COLORS.tickLight },
        },
        y: {
          type: 'linear',
          title: yAxisLabel ? { display: true, text: yAxisLabel } : undefined,
          grid: { color: dark ? CHART_COLORS.gridDark : CHART_COLORS.gridLight },
          ticks: { color: dark ? CHART_COLORS.tickDark : CHART_COLORS.tickLight },
        },
      },
      plugins: {
        legend: { display: series.length > 1, position: 'top' },
        datalabels: { display: false },
      },
    }),
    [dark, xAxisLabel, yAxisLabel, series.length],
  )

  return (
    <div
      className={className}
      style={{ height, width: '100%' }}
      role="img"
      aria-label={ariaLabel}
    >
      <Chart type="scatter" data={data} options={options} />
    </div>
  )
}
